import type { UserQuestDetail } from "Infrastructure/Api/Api";
import { useDateFnsFormatString } from "Infrastructure/Hooks/DateFns/useDateFnsFormatString";
import { useRequestStatus } from "Infrastructure/Hooks/useRequestStatus";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Stack, Typography, useTheme } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useMemo } from "react";
import { RewardBadge } from "./Shared/RewardBadge";
import { StatusChip } from "./Shared/StatusChip";
import { UserChip } from "./Shared/UserChip";

export const CustomCard = ({
  request,
  onClick,
}: {
  request: UserQuestDetail;
  onClick?: () => void;
}) => {
  const { colors, customSpacing, radius } = useTheme();
  const { t } = useResource();
  const dateFormat = useDateFnsFormatString();
  const statusConfig = useRequestStatus(request.progress);

  const formattedDate = useMemo(() => {
    if (!request.dateStart) {
      return "";
    }
    const date = parseISO(request.dateStart.split("T")[0]);
    return isValid(date) ? format(date, dateFormat) : "";
  }, [request.dateStart, dateFormat]);

  return (
    <Stack
      onClick={onClick}
      sx={{
        backgroundColor: colors.background.primary,
        border: `1px solid ${colors.border.primary}`,
        borderRadius: radius.lg,
        padding: customSpacing.lg,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        height: 130,
        position: "relative",
        "&:hover": onClick
          ? {
              backgroundColor: colors.background.hover,
              borderColor: colors.border.secondary,
            }
          : {},
      }}
    >
      <RewardBadge value={request.rewardCoins} />

      <Typography variant="body2" color="text.secondary" mb={1}>
        {formattedDate}
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {request.questName || t(Resources.RequestCards.Common.UnknownQuest)}
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt="auto"
      >
        <UserChip name={request.userName} />
        <StatusChip status={statusConfig} />
      </Stack>
    </Stack>
  );
};
